<template>
  <transition v-bind="modalTransition">
    <div v-if="isOpen" class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
      <div class="mx-auto max-w-lg bg-white p-6 shadow-4">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-xl font-semibold">{{ t('globals.buttons.edit') }} {{ streamName }}</h2>
          <button @click="cancel" class="hover:text-slate-400">
            <IcClose />
          </button>
        </div>
        <form @submit.prevent="handleSave">
          <div class="mb-4">
            <label for="name" class="block text-sm font-medium">
              {{ t('groups.name') }}
            </label>
            <input
              v-model="groupName"
              type="text"
              id="name"
              class="mt-1 block w-full border border-bodydark2 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              :placeholder="t('placeholders.sensors.example')" />
            <div v-if="errors.groupName" class="text-sm text-red">
              {{ errors.groupName }}
            </div>
          </div>
          <div class="mb-4">
            <label for="description" class="block text-sm font-medium">
              {{ t('groups.description') }}
            </label>
            <textarea
              v-model="groupObservation"
              id="description"
              rows="3"
              class="mt-1 block w-full border border-bodydark2 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              :placeholder="t('placeholders.sensors.observation')" />
          </div>
          <div class="mb-4 flex items-center">
            <button @click="isAlarmModalOpen = true" type="button" class="text-blue hover:text-blue-600">
              {{ t('groups.alarms_title') }}
            </button>
            <div class="group relative ml-2 flex items-center">
              <div
                class="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-64 -translate-x-1/2 bg-black px-3 py-2 text-xs text-white opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                {{ t('groups.tooltip_line_setup') }}<br />
              </div>
              <span tabindex="0" class="cursor-pointer text-lg text-gray-500">
                <IcCircleInfo />
              </span>
            </div>
          </div>
          <div class="mb-4">
            <label class="mb-2 block text-sm font-medium">
              {{ t('sensors.name') }}
            </label>
            <MultiSelect
              :options="sensorOptions"
              :errorText="errors.sensorIdsList"
              @update:selectedValues="handleSelectedSensors" />
          </div>
          <div class="mb-4">
            <label class="mb-2 block text-sm font-medium">
              {{ t('groups.shifts') }}
            </label>
            <MultiSelect
              :options="shiftOptions"
              @update:selectedValues="handleSelectedShifts" />
          </div>
          <div class="mt-6 flex justify-end space-x-4">
            <button
              type="submit"
              class="bg-blue px-4 py-2 text-white shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
              <span v-if="isLoading">
                <ButtonLoader />
              </span>
              <span v-else>
                {{ t('globals.buttons.save') }}
              </span>
            </button>
            <button @click="cancel" type="button" class="text-slate-500 hover:text-slate-400">
              {{ t('globals.buttons.cancel') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </transition>
  <AlarmGroupModal
    :is-open="isAlarmModalOpen"
    :group-id="props.groupId"
    @close="isAlarmModalOpen = false"
    @saved="updateAlarmSettings" />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from '@/composable/useI18n'
import IcClose from '@/components/Icons/IcClose.vue'
import * as yup from 'yup'
import { useSensorStore } from '@/store/sensor'
import { useGroupStore } from '@/store/group'
import { useCompanyStore } from '@/store/company'
import { useSuccessStore } from '@/store/success'
import ButtonLoader from '@/components/Globals/Feedbacks/ButtonLoader.vue'
import { modalTransition } from '@/plugins/transitions'
import IcCircleInfo from '@/components/Icons/IcCircleInfo.vue'
import AlarmGroupModal from './AlarmGroupModal.vue'
import MultiSelect from '@/components/Forms/MultiSelect.vue'

const { t } = useI18n()

interface IMultiSelectOption {
  id: number
  value: number
  text: string
  selected: boolean
}

const validationSchema = yup.object().shape({
  groupName: yup.string().required(t('errors.required', { label: t('groups.name') })),
  is_line_setup: yup.boolean(),
  active_power_outage_alarm: yup.boolean(),
  sensorIdsList: yup
    .array()
    .of(yup.number())
    .min(1, t('errors.must_have', { label: t('sensors.name'), count: '1' })),
})

const props = defineProps<{
  isOpen: boolean
  streamName: string
  groupId: number
}>()

const emit = defineEmits(['close', 'confirm'])

const storeSensor = useSensorStore()
const companyStore = useCompanyStore()
const sensors = computed(() => storeSensor.sensors)
const shifts = computed(() => companyStore.shifts)
const sensorIdsList = ref<number[]>([])
const shiftIdsList = ref<number[]>([])
const errors = ref<{ [key: string]: string | undefined }>({})
const groupName = ref('')
const groupObservation = ref<string>('')
const is_line_setup = ref(false)
const active_power_outage_alarm = ref(false)
const isLoading = ref(false)

const storeGroup = useGroupStore()

const fetchSensors = storeSensor.fetch
let selectionOptionsPromise: Promise<unknown[]> | null = null

function loadSelectionOptions() {
  if (!selectionOptionsPromise) {
    selectionOptionsPromise = Promise.all([
      fetchSensors(),
      companyStore.fetchCompanyShifts(),
    ])
  }

  return selectionOptionsPromise
}

const sensorOptions = computed<IMultiSelectOption[]>(() =>
  sensors.value.map((sensor) => ({
    id: sensor.id,
    value: sensor.id,
    text: sensor.name,
    selected: sensorIdsList.value.includes(sensor.id),
  })),
)

const shiftOptions = computed<IMultiSelectOption[]>(() =>
  shifts.value.map((shift) => ({
    id: shift.id,
    value: shift.id,
    text: shift.name,
    selected: shiftIdsList.value.includes(shift.id),
  })),
)

const isAlarmModalOpen = ref(false)
function updateAlarmSettings(updatedSettings: { is_line_setup: boolean; active_power_outage_alarm: boolean }) {
  is_line_setup.value = updatedSettings.is_line_setup
  active_power_outage_alarm.value = updatedSettings.active_power_outage_alarm
}

onMounted(async () => {
  await loadSelectionOptions()
  if (props.isOpen && props.groupId) {
    loadGroupData()
  }
})

watch(
  () => props.isOpen,
  async (newValue) => {
    if (newValue && props.groupId) {
      await loadSelectionOptions()
      loadGroupData()
    }
  },
)

function cancel() {
  emit('close')
}

const handleSave = async () => {
  if (await validate()) {
    await saveGroup()
  }
}

function handleSelectedSensors(values: Array<number | string>) {
  sensorIdsList.value = values.map(Number)
  errors.value.sensorIdsList = undefined
}

function handleSelectedShifts(values: Array<number | string>) {
  shiftIdsList.value = values.map(Number)
}

const saveGroup = async () => {
  const isValid = await validate()

  if (!isValid) return

  isLoading.value = true

  try {
    await storeGroup.editGroup({
      id: props.groupId,
      name: groupName.value,
      observation: groupObservation.value,
      is_line_setup: is_line_setup.value,
      active_power_outage_alarm: active_power_outage_alarm.value,
      sensor_ids: sensorIdsList.value,
      shift_ids: shiftIdsList.value,
    })

    useSuccessStore().setSuccess([t('globals.changed_with_success')])

    groupName.value = ''
    groupObservation.value = ''
    sensorIdsList.value = []
    shiftIdsList.value = []
    errors.value = {}
    emit('close')
  } catch {
    // Erro ao salvar grupo
  } finally {
    isLoading.value = false
  }
}

const validate = async () => {
  errors.value = {}
  try {
    await validationSchema.validate(
      {
        groupName: groupName.value,
        sensorIdsList: sensorIdsList.value,
      },
      { abortEarly: false },
    )
    return true
  } catch (error) {
    if (error instanceof yup.ValidationError) {
      error.inner.forEach((err) => {
        if (err.path) {
          errors.value[err.path] = err.message
        }
      })
    }
    return false
  }
}

function loadGroupData() {
  const group = storeGroup.groups.find((g) => g.id === props.groupId)
  if (group) {
    groupName.value = group.name
    groupObservation.value = group.observation ?? ''
    is_line_setup.value = group.is_line_setup ?? false
    active_power_outage_alarm.value = group.active_power_outage_alarm ?? false
    sensorIdsList.value = group.sensors.map((sensor) => sensor.id)
    shiftIdsList.value = group.shift_ids ?? group.shifts?.map((shift) => shift.id) ?? []
  }
}

watch(groupName, () => {
  errors.value.groupName = undefined
})
</script>
